import { useEffect, useMemo, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { assetHref, hallMapStyles } from '@lmac/ui';
import {
  clubDate,
  formatCents,
  paths,
  type PublicMapChunk,
  type PublicTable,
  type SwapMeetEvent,
} from '@lmac/shared';
import { db, ensureSession } from './firebase';
import { Reserve } from './Reserve';
import { Preview } from './Preview';
import { reserve } from './reserveApi';
import { requestParentScroll } from './embedHeight';

import hall from '@lmac/data/bingo-hall.json';
import eventSeed from '@lmac/data/event-2026-11-21.json';

/**
 * The vendor-facing app, embedded in lmacky.org.
 *
 * It prefers the live `publicMap` document. Until the club publishes one it
 * falls back to the layout bundled at build time, so the whole reservation
 * flow can be reviewed before anything is written to the database.
 */

const event = eventSeed as unknown as SwapMeetEvent;

type Load =
  | { state: 'loading' }
  | { state: 'ready'; map: PublicMapChunk; live: boolean };

/** The bundled layout, shaped like the document a publish would produce. */
function seedMap(): PublicMapChunk {
  const h = hall as unknown as {
    map: { width: number; height: number };
    content: PublicMapChunk['content'];
    background: PublicMapChunk['background'];
    outlets: PublicMapChunk['outlets'];
    checkInTables: PublicMapChunk['checkInTables'];
    tables: {
      label: string; section: string; x: number; y: number; w: number; h: number;
      status: 'available' | 'blocked'; nearPower: boolean; priceOverride: number | null;
    }[];
  };
  const tables: PublicTable[] = h.tables.map((t) => ({
    id: `t-${t.label}`,
    label: t.label,
    section: t.section,
    x: t.x, y: t.y, w: t.w, h: t.h,
    price: t.priceOverride ?? event.pricePerTable,
    nearPower: t.nearPower,
    status: t.status,
  }));
  return {
    eventId: event.id,
    tables,
    map: h.map,
    content: h.content,
    background: {
      ...h.background,
      href: assetHref(h.background.href, import.meta.env.BASE_URL),
    },
    outlets: h.outlets,
    checkInTables: h.checkInTables,
    updatedAt: Date.now(),
  };
}

export function App() {
  const [load, setLoad] = useState<Load>({ state: 'loading' });
  const fallback = useMemo(seedMap, []);

  /**
   * `?mode=preview` renders the compact availability view for the club's own
   * page; anything else renders the full reservation flow.
   *
   * A query parameter rather than a second build: one deploy, one bundle
   * already cached, and switching from the preview to the flow is then a state
   * change rather than a page load — the vendor keeps their place on the club
   * page instead of navigating away from it.
   */
  const [mode, setMode] = useState<'preview' | 'reserve'>(() =>
    new URLSearchParams(window.location.search).get('mode') === 'preview'
      ? 'preview'
      : 'reserve',
  );

  useEffect(() => {
    let stop = () => {};

    // A read failure must not leave a blank page. The layout ships in the
    // bundle regardless, so fall back to it and mark the map as not live.
    const usePreview = () =>
      setLoad({ state: 'ready', map: fallback, live: false });

    ensureSession()
      .then(() => {
        stop = onSnapshot(
          doc(db, paths.publicMapChunk(event.id)),
          (snap) => {
            if (!snap.exists()) {
              usePreview();
              return;
            }
            const doc = snap.data() as PublicMapChunk;
            setLoad({
              state: 'ready',
              map: {
                ...doc,
                background: {
                  ...doc.background,
                  href: assetHref(doc.background.href, import.meta.env.BASE_URL),
                },
              },
              live: true,
            });
          },
          usePreview,
        );
      })
      .catch(usePreview);

    return () => stop();
  }, [fallback]);

  return (
    <>
      <style>{hallMapStyles}</style>
      <main className="app">
        {load.state === 'ready' && mode === 'preview' && (
          <Preview
            event={event}
            map={load.map}
            onStart={() => {
              setMode('reserve');
              // The embed may sit well down a long club page; bring it into
              // view so the vendor is not left looking at the flyer above it.
              requestParentScroll();
            }}
          />
        )}

        {mode === 'reserve' && (
        <>
        <header className="app__head">
          <p className="app__eyebrow">Lexington Model Airplane Club</p>
          <h1 className="app__title">{event.name}</h1>
          <p className="app__when">
            {event.dateLabel}
            <br />
            {event.venueAddress} &middot; {event.setupLabel}
          </p>
        </header>

        <dl className="app__bar">
          <div>
            <dt>Tables in advance</dt>
            <dd>{formatCents(event.pricePerTable)}</dd>
          </div>
          <div>
            <dt>On the day</dt>
            <dd>{formatCents(event.dayOfPricePerTable)}</dd>
          </div>
          <div>
            <dt>Advance rate until</dt>
            <dd>
              {clubDate(event.reservationsCloseAt, 'short')}
            </dd>
          </div>
          <div><dt>Admission</dt><dd>{event.admissionLabel?.split(' ')[0] ?? '$5'}</dd></div>
        </dl>

        {load.state === 'loading' && <p className="app__status">Loading the hall…</p>}
        {load.state === 'ready' && mode === 'reserve' && (
          <Reserve
            event={event}
            map={load.map}
            // Live only against a published map. Without one there are no
            // tables in the database to claim, so the flow stays a preview
            // rather than failing at the last step.
            onSubmit={load.live ? reserve : undefined}
          />
        )}

        <p className="app__note">
          Questions? Email{' '}
          <a href={`mailto:${event.contactEmail}`}>{event.contactEmail}</a>.
          Children under 12 get in free.
        </p>
        </>
        )}
      </main>
    </>
  );
}
